import { useState } from "react";
import { ListOrdered, List, ChevronDown, CornerDownRight } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";

const NUMBER_STYLES = [
  { id: "decimal", sample: ["1.", "2.", "3."] },
  { id: "decimal-leading-zero", sample: ["01.", "02.", "03."] },
  { id: "lower-roman", sample: ["i.", "ii.", "iii."] },
  { id: "upper-roman", sample: ["I.", "II.", "III."] },
  { id: "lower-alpha", sample: ["a.", "b.", "c."] },
  { id: "upper-alpha", sample: ["A.", "B.", "C."] },
];
const BULLET_STYLES = [
  { id: "disc", sample: ["\u25cf", "\u25cf", "\u25cf"] },
  { id: "circle", sample: ["\u25cb", "\u25cb", "\u25cb"] },
  { id: "square", sample: ["\u25aa", "\u25aa", "\u25aa"] },
];

/** A three-line thumbnail of what a marker style looks like, so the choice is
 *  made by eye rather than by reading "lower-roman". */
const StyleTile = ({ style, active, onPick, kind }) => (
  <button type="button" data-testid={`kb-list-style-${style.id}`}
    onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(style.id)}
    title={style.id}
    className={`flex flex-col gap-1 rounded-md border px-2.5 py-2 transition-colors
      ${active ? "border-primary bg-primary/10" : "border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
    {style.sample.map((s, i) => (
      <span key={i} className="flex items-center gap-1.5">
        <span className={`w-6 text-right text-[10px] leading-none ${active ? "text-primary" : "text-slate-500 dark:text-slate-400"}`}>{s}</span>
        <span className="h-[2px] w-8 rounded-full bg-slate-300" />
      </span>
    ))}
    <span className="sr-only">{kind} {style.id}</span>
  </button>
);

/** `editor.isActive` wants the whole selection inside the node, so a
 *  select-all across a list plus the paragraph after it reads as "not in a
 *  list". Walking up from the caret is what the author means. */
const inList = (editor, name) => {
  const { $head } = editor.state.selection;
  for (let d = $head.depth; d > 0; d -= 1) {
    if ($head.node(d).type.name === name) return true;
  }
  return false;
};

const listAttrs = (editor, name) => {
  const { $head } = editor.state.selection;
  for (let d = $head.depth; d > 0; d -= 1) {
    if ($head.node(d).type.name === name) return $head.node(d).attrs;
  }
  return {};
};

/** The numbered list the caret's list follows, if any — a screenshot or a line
 *  of prose between steps ends a list, and the next one restarting at 1 is the
 *  thing authors keep fixing by hand. Only looks back over three blocks, so an
 *  unrelated list further up the page is left alone. */
export const previousListNumber = (editor) => {
  const { $head } = editor.state.selection;
  let depth = null;
  for (let d = $head.depth; d > 0; d -= 1) {
    if ($head.node(d).type.name === "orderedList") { depth = d; break; }
  }
  const parent = depth ? $head.node(depth - 1) : $head.node(Math.max(0, $head.depth - 1));
  const from = depth ? $head.index(depth - 1) : $head.index(Math.max(0, $head.depth - 1)) + 1;
  let gap = 0;
  for (let i = from - 1; i >= 0 && gap <= 3; i -= 1) {
    const node = parent.child(i);
    if (node.type.name === "orderedList") {
      const start = Number(node.attrs.start ?? 1) || 1;
      return { next: start + node.childCount, listStyle: node.attrs.listStyle || null };
    }
    if (node.type.name === "bulletList") return null;
    gap += 1;
  }
  return null;
};

/** Pick up where the list above left off, keeping its marker style. */
export const continueNumbering = (editor, { announce = true } = {}) => {
  const prev = previousListNumber(editor);
  if (!prev || prev.next <= 1) return false;
  const attrs = { start: prev.next };
  if (prev.listStyle) attrs.listStyle = prev.listStyle;
  editor.chain().focus().updateAttributes("orderedList", attrs).run();
  if (announce) toast.success(`Carrying on at ${prev.next}`);
  return true;
};

/** List properties — marker style for both kinds of list, plus where a numbered
 *  list starts and which way it counts. Everything applies to the list the
 *  cursor is in; picking a style when you're not in a list starts one. */
export function KbListTools({ editor }) {
  const [open, setOpen] = useState(false);
  const inOrdered = inList(editor, "orderedList");
  const inBullet = inList(editor, "bulletList");
  const attrs = inOrdered ? listAttrs(editor, "orderedList")
    : inBullet ? listAttrs(editor, "bulletList") : {};
  const start = Number(attrs.start ?? 1) || 1;
  const carryOn = inOrdered ? previousListNumber(editor) : null;

  const pickNumber = (id) => {
    const chain = editor.chain().focus();
    if (!inOrdered) chain.toggleOrderedList();
    chain.updateAttributes("orderedList", { listStyle: id }).run();
  };
  const pickBullet = (id) => {
    const chain = editor.chain().focus();
    if (!inBullet) chain.toggleBulletList();
    chain.updateAttributes("bulletList", { listStyle: id }).run();
  };
  const setStart = (n) => {
    const value = Math.max(1, Math.min(Number(n) || 1, 9999));
    // No `.focus()` here — it would pull the caret back into the article after
    // every digit, so typing "12" would leave you editing the body.
    editor.chain().updateAttributes("orderedList", { start: value }).run();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" title="List properties" data-testid="kb-rte-list-props"
          onMouseDown={(e) => e.preventDefault()}
          className={`flex h-8 items-center gap-0.5 rounded-md px-1 text-slate-600 dark:text-slate-300 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800
            ${inOrdered || inBullet ? "bg-primary/10 text-primary" : ""}`}>
          <ChevronDown className="h-3 w-3 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="bottom" sideOffset={6} collisionPadding={12}
        data-testid="kb-list-props-popover" className="w-auto space-y-3 p-3"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}>
        <div>
          <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            <ListOrdered className="h-3.5 w-3.5" /> Numbered
          </p>
          <div className="grid grid-cols-3 gap-1.5">
            {NUMBER_STYLES.map((s) => (
              <StyleTile key={s.id} style={s} kind="numbered" onPick={pickNumber}
                active={inOrdered && (attrs.listStyle || "decimal") === s.id} />
            ))}
          </div>
        </div>

        <div className="border-t border-slate-100 dark:border-slate-800 pt-3">
          <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            <List className="h-3.5 w-3.5" /> Bulleted
          </p>
          <div className="grid grid-cols-3 gap-1.5">
            {BULLET_STYLES.map((s) => (
              <StyleTile key={s.id} style={s} kind="bulleted" onPick={pickBullet}
                active={inBullet && (attrs.listStyle || "disc") === s.id} />
            ))}
          </div>
        </div>

        <div className="space-y-2 border-t border-slate-100 dark:border-slate-800 pt-3">
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">List properties</p>
          {carryOn && (
            <button type="button" data-testid="kb-list-continue"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => continueNumbering(editor)}
              className="flex w-full items-center gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-2 py-1.5 text-xs font-medium text-primary hover:bg-primary/10">
              <CornerDownRight className="h-3.5 w-3.5" />
              Carry on from the list above (starts at {carryOn.next})
            </button>
          )}
          <label className="flex items-center justify-between gap-3 text-xs text-slate-600 dark:text-slate-300">
            Start at
            <input type="number" min="1" max="9999" value={start} disabled={!inOrdered}
              data-testid="kb-list-start"
              onChange={(e) => setStart(e.target.value)}
              className="h-7 w-20 rounded-md border border-slate-200 dark:border-slate-800 px-2 text-xs disabled:opacity-40" />
          </label>
          <label className="flex cursor-pointer items-center justify-between gap-3 text-xs text-slate-600 dark:text-slate-300">
            Reversed order
            <Switch checked={!!attrs.reversed} disabled={!inOrdered}
              data-testid="kb-list-reversed"
              onCheckedChange={(v) => editor.chain().focus()
                .updateAttributes("orderedList", { reversed: v || null }).run()} />
          </label>
          {!inOrdered && (
            <p className="text-[11px] text-slate-400 dark:text-slate-500" data-testid="kb-list-props-hint">
              Put the cursor in a numbered list to set where it starts.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
